// ===================================
// EMBEDDED PUBLIC CHAT WIDGET COMPONENT - For iframe embedding
// src/components/PublicChatWidget.tsx
// ===================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import PublicChat from "./PublicChat";
import { X, Minimize2, Maximize2, Bot } from "lucide-react";

interface PublicChatWidgetProps {
  theme?: "blue" | "purple" | "green";
  customTitle?: string;
  className?: string;
  isEmbedded?: boolean;
}

const PublicChatWidget: React.FC<PublicChatWidgetProps> = ({
  theme = "blue",
  customTitle = "Customer Support",
  className = "",
  isEmbedded = false,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatKey, setChatKey] = useState(0); // Force re-render of chat component
  const chatInitialized = useRef(false);

  // Get URL parameters
  const params = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );

  // Use URL parameters for configuration when embedded
  const finalTheme =
    isEmbedded && params.theme
      ? (params.theme as "blue" | "purple" | "green")
      : theme;
  const finalTitle =
    isEmbedded && params.title ? decodeURIComponent(params.title) : customTitle;
  const businessId = params.businessId;

  // Theme configurations
  const themeConfig = {
    blue: {
      primary: "from-blue-600 to-purple-600",
      hover: "hover:from-blue-700 hover:to-purple-700",
      text: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
    },
    purple: {
      primary: "from-purple-600 to-pink-600",
      hover: "hover:from-purple-700 hover:to-pink-700",
      text: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
    },
    green: {
      primary: "from-green-600 to-emerald-600",
      hover: "hover:from-green-700 hover:to-emerald-700",
      text: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
    },
  };

  const currentTheme = themeConfig[finalTheme];

  const handleCloseChat = useCallback(() => {
    if (isEmbedded && window.parent) {
      // Notify parent to close the iframe
      window.parent.postMessage(
        {
          source: "publicChatWidget",
          type: "close",
        },
        "*"
      );
    } else {
      setIsMinimized(false);
      chatInitialized.current = false;
      document.body.style.overflow = "unset";
      // Reset chat component on close
      setTimeout(() => {
        setChatKey((prev) => prev + 1);
      }, 300);
    }
  }, [isEmbedded]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isMinimized) {
        handleCloseChat();
      }
    };

    document.addEventListener("keydown", handleEscape);

    // Don't prevent body scroll in embedded mode
    if (!isEmbedded && !isMinimized) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (!isEmbedded) {
        document.body.style.overflow = "unset";
      }
    };
  }, [isMinimized, isEmbedded, handleCloseChat]);

  // Clean up body scroll on unmount
  useEffect(() => {
    return () => {
      if (!isEmbedded) {
        document.body.style.overflow = "unset";
      }
    };
  }, [isEmbedded]);

  // Initialize chat when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setChatKey((prev) => prev + 1);
      chatInitialized.current = true;

      // Notify parent that chat is ready (for embedded mode)
      if (isEmbedded && window.parent) {
        window.parent.postMessage(
          {
            source: "publicChatWidget",
            type: "ready",
          },
          "*"
        );
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isEmbedded]);

  // Communicate with parent window (for embedded mode)
  useEffect(() => {
    if (isEmbedded && window.parent) {
      const sendMessage = (data: any) => {
        window.parent.postMessage(
          {
            source: "publicChatWidget",
            ...data,
          },
          "*"
        );
      };

      // Send initial height
      sendMessage({
        type: "resize",
        height: isMinimized ? 80 : 600,
      });

      // Update height when minimized state changes
      sendMessage({
        type: "resize",
        height: isMinimized ? 80 : 600,
      });
    }
  }, [isEmbedded, isMinimized]);

  const handleMinimizeChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newIsMinimized = !isMinimized;
    setIsMinimized(newIsMinimized);

    // Manage body scroll based on minimized state (only for non-embedded)
    if (!isEmbedded) {
      if (newIsMinimized) {
        document.body.style.overflow = "unset";
      } else {
        document.body.style.overflow = "hidden";
      }
    }

    // Notify parent of size change in embedded mode
    if (isEmbedded && window.parent) {
      window.parent.postMessage(
        {
          source: "publicChatWidget",
          type: "resize",
          height: newIsMinimized ? 80 : 600,
        },
        "*"
      );
    }
  };

  // Listen for chat events to update widget state
  useEffect(() => {
    const handleChatReady = () => {
      chatInitialized.current = true;
    };

    const handleNewMessage = () => {
      if (isMinimized) {
        setUnreadCount((prev) => prev + 1);
      }
    };

    // Custom events from PublicChat component
    window.addEventListener("chatReady", handleChatReady);
    window.addEventListener("newChatMessage", handleNewMessage);

    return () => {
      window.removeEventListener("chatReady", handleChatReady);
      window.removeEventListener("newChatMessage", handleNewMessage);
    };
  }, [isMinimized]);

  // If embedded but no business ID, show error
  if (isEmbedded && !businessId) {
    return (
      <div
        className={`w-full h-full bg-white rounded-xl shadow-2xl ${className} flex items-center justify-center`}
      >
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Chat Configuration Error
          </h3>
          <p className="text-gray-600 mb-4">
            The chat requires a missing identifier. Please contact the website
            administrator to get this resolved.
          </p>
          {/* <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">
              Missing:{" "}
              <code className="bg-gray-200 px-1 rounded">businessId</code>{" "}
              parameter
            </p>
          </div> */}
        </div>
      </div>
    );
  }

  // For embedded mode, render directly without modal overlay
  if (isEmbedded) {
    return (
      <div
        className={`w-full h-full bg-white rounded-xl shadow-2xl ${className}`}
      >
        {/* Chat Header */}
        <div
          className={`bg-[#8b3dff] text-white p-4 rounded-t-xl flex items-center justify-between`}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{finalTitle}</h3>
              <p className="text-sm text-blue-100">
                {isMinimized
                  ? "Click to expand"
                  : chatInitialized.current
                  ? "We typically reply instantly"
                  : "Starting up..."}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Minimize/Maximize Button */}
            <button
              onClick={handleMinimizeChat}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label={isMinimized ? "Maximize chat" : "Minimize chat"}
            >
              {isMinimized ? (
                <Maximize2 className="w-5 h-5" />
              ) : (
                <Minimize2 className="w-5 h-5" />
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={handleCloseChat}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Chat Content */}
        {!isMinimized && (
          <div
            className="flex flex-col"
            style={{ height: "calc(600px - 72px)" }}
          >
            <PublicChat
              key={chatKey}
              isEmbedded={true}
              className="flex-1 rounded-none border-none"
              onClose={handleCloseChat}
              businessId={businessId}
            />
          </div>
        )}

        {/* Minimized state with expand hint */}
        {isMinimized && (
          <div
            className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-xl cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={handleMinimizeChat}
          >
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Chat minimized - Click to expand</span>
              <Maximize2 className="w-4 h-4" />
            </div>
            {unreadCount > 0 && (
              <div className="mt-2 text-xs text-blue-600 font-medium">
                {unreadCount} new message{unreadCount > 1 ? "s" : ""}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // For non-embedded mode (modal overlay - kept for backward compatibility)
  return (
    <div className={`fixed inset-0 z-50 ${className}`}>
      {/* Backdrop overlay - only show when not minimized */}
      {!isMinimized && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
          onClick={handleCloseChat}
        />
      )}

      {/* Chat container */}
      <div className="fixed transition-all duration-300 inset-0 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div
          className="relative bg-white rounded-xl shadow-2xl transform transition-all duration-300 w-full max-w-4xl h-full max-h-[600px]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Chat Header */}
          <div
            className={`bg-[#8b3dff] text-white p-4 rounded-t-xl flex items-center justify-between`}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{finalTitle}</h3>
                <p className="text-sm text-blue-100">
                  {chatInitialized.current
                    ? "We typically reply instantly"
                    : "Starting up..."}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Close Button */}
              <button
                onClick={handleCloseChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Chat Content */}
          <div
            className="flex flex-col"
            style={{ height: "calc(600px - 72px)" }}
          >
            <PublicChat
              key={chatKey}
              isEmbedded={false}
              className="flex-1 rounded-none border-none"
              onClose={handleCloseChat}
              businessId={businessId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicChatWidget;
